import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Users, Zap, Shield, TrendingUp, Globe } from 'lucide-react';
import { CILogo } from '../components/CILogo';

export default function AboutUs() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <CILogo size={40} showText={true} theme="light" />
          </button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">About Commerce Index</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            AI-powered deal intelligence platform helping merchants maximize conversions and consumers find the best prices.
          </p>
        </div>

        {/* Company Info */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 md:p-12 mb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Company</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Commerce Index is a product of <strong>Dispatch Global Services Inc (d/b/a DGS or Sagisu)</strong>, 
              a technology company focused on building intelligent commerce solutions.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Founded in 2024, we're on a mission to bring transparency and intelligence to e-commerce through 
              AI-powered deal scoring, helping millions of shoppers make better purchasing decisions and thousands 
              of merchants optimize their pricing strategies.
            </p>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white border border-gray-200 rounded-xl p-8">
            <Target className="w-12 h-12 text-blue-600 mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Our Mission</h3>
            <p className="text-gray-700 leading-relaxed">
              To empower merchants with AI-driven insights for optimal pricing and help consumers discover 
              legitimate deals through transparent, data-backed CI Scores.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-8">
            <Globe className="w-12 h-12 text-purple-600 mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Our Vision</h3>
            <p className="text-gray-700 leading-relaxed">
              To become the world's most trusted deal intelligence platform, powering commerce decisions 
              for millions of merchants and consumers globally.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Innovation</h4>
              <p className="text-sm text-gray-600">
                Leveraging cutting-edge AI (GPT-5, machine learning) to solve real commerce problems
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Trust</h4>
              <p className="text-sm text-gray-600">
                Transparent scoring, honest pricing, and commitment to data privacy and security
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Customer Success</h4>
              <p className="text-sm text-gray-600">
                Your success is our success. We're dedicated to helping you achieve your goals
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 md:p-12 mb-16 text-white">
          <h2 className="text-3xl font-bold mb-8 text-center">Commerce Index by the Numbers</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">1000+</div>
              <div className="text-blue-100">AI-Scored Deals</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100K+</div>
              <div className="text-blue-100">Deal Seekers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">150+</div>
              <div className="text-blue-100">Merchants</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">18%</div>
              <div className="text-blue-100">Avg Conversion Increase</div>
            </div>
          </div>
        </div>

        {/* Technology */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Powered by Advanced Technology</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-3">AI & Machine Learning</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">GPT-5 powered deal recommendations</span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Proprietary CI Score algorithm (0-1000)</span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Real-time price intelligence across retailers</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-3">Enterprise Infrastructure</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">SOC 2 compliant security</span>
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">99.9% uptime SLA (Enterprise tier)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">GDPR & CCPA compliant</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gray-50 rounded-2xl p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Join hundreds of merchants using Commerce Index to optimize deals and increase conversions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/merchant/login')}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              Start as Merchant
            </button>
            <button
              onClick={() => navigate('/deals')}
              className="px-8 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
            >
              Browse Deals
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-600">
          <p>© 2024 Dispatch Global Services Inc (d/b/a DGS or Sagisu). All rights reserved.</p>
          <p className="mt-1">Commerce Index is a product of Dispatch Global Services Inc.</p>
        </div>
      </footer>
    </div>
  );
}
